import { readCapabilities, type GpuCapabilities } from './capabilities.js';
import { BufferPool, GpuImage, TexturePool, UniformRing } from './resources.js';
import { ShaderLibrary, type KernelRequest } from './shader-library.js';
import { SHADER_SOURCES } from './shaders/index.js';
import type { TexFormat } from './types.js';

/** Optional features we ask for when they exist; none of them are required. */
const OPTIONAL_FEATURES: GPUFeatureName[] = [
  'shader-f16',
  'timestamp-query',
  'subgroups' as GPUFeatureName,
];

export interface GpuContextOptions {
  /**
   * Adopt an existing device instead of requesting one.
   *
   * Used when ONNX Runtime already owns a device: sharing it is what makes the
   * zero-copy tensor handoff possible.
   */
  device?: GPUDevice;
  powerPreference?: GPUPowerPreference;
  /** Features to require; creation fails if the adapter lacks them. */
  requiredFeatures?: GPUFeatureName[];
  /** Texture pool budget in bytes. */
  texturePoolBudget?: number;
  label?: string;
  onDeviceLost?: (info: GPUDeviceLostInfo) => void;
  onUncapturedError?: (error: GPUError) => void;
}

export type BindResource = GPUTextureView | GPUBufferBinding | GPUBuffer | GPUSampler | GpuImage;

export interface PassTiming {
  label: string;
  /** Nanoseconds, or `null` when the device lacks `timestamp-query`. */
  durationNs: number | null;
}

/**
 * Owns the `GPUDevice` and everything derived from it: shader library, resource
 * pools, the uniform ring and the command encoder the operators record into.
 *
 * Operators never submit on their own. They record dispatches into the context's
 * open compute pass and the caller decides when to `flush()`, so a full matte
 * refresh (~40 dispatches) costs a single submit.
 */
export class GpuContext {
  readonly shaders: ShaderLibrary;
  readonly textures: TexturePool;
  readonly buffers: BufferPool;
  readonly uniforms: UniformRing;
  readonly caps: GpuCapabilities;

  #encoder: GPUCommandEncoder | null = null;
  #pass: GPUComputePassEncoder | null = null;
  #lost: GPUDeviceLostInfo | null = null;
  #destroyed = false;

  // --- profiling -----------------------------------------------------------
  #profiling = false;
  #querySet: GPUQuerySet | null = null;
  #queryCapacity = 0;
  #queryIndex = 0;
  #queryLabels: string[] = [];
  #lastTimings: PassTiming[] = [];

  /**
   * False when the device was handed to us (ONNX Runtime owns it), in which case
   * destroying it here would take the inference runtime down with us.
   */
  readonly #ownsDevice: boolean;

  private constructor(
    readonly device: GPUDevice,
    isFallbackAdapter: boolean,
    options: GpuContextOptions,
  ) {
    this.#ownsDevice = options.device === undefined;
    this.caps = readCapabilities(device, isFallbackAdapter);
    this.shaders = new ShaderLibrary(device);
    this.shaders.registerAll(SHADER_SOURCES);
    this.textures = new TexturePool(device, options.texturePoolBudget);
    this.buffers = new BufferPool(device);
    this.uniforms = new UniformRing(device);

    void device.lost.then((info) => {
      this.#lost = info;
      options.onDeviceLost?.(info);
    });
    device.onuncapturederror = (event) => {
      options.onUncapturedError?.(event.error);
      if (!options.onUncapturedError) console.error('[gpu-core] uncaptured error:', event.error);
    };
  }

  static async create(options: GpuContextOptions = {}): Promise<GpuContext> {
    if (options.device) return new GpuContext(options.device, false, options);

    if (typeof navigator === 'undefined' || !navigator.gpu) {
      throw new Error('[gpu-core] WebGPU is not available in this browser.');
    }
    const adapter = await navigator.gpu.requestAdapter({
      powerPreference: options.powerPreference ?? 'high-performance',
    });
    if (!adapter) {
      throw new Error(
        '[gpu-core] no WebGPU adapter. On Linux/older GPUs, check chrome://gpu for a blocklist entry.',
      );
    }

    const required = options.requiredFeatures ?? [];
    for (const feature of required) {
      if (!adapter.features.has(feature)) {
        throw new Error(`[gpu-core] adapter is missing required feature "${feature}"`);
      }
    }
    const features = new Set<GPUFeatureName>(required);
    for (const feature of OPTIONAL_FEATURES) {
      if (adapter.features.has(feature)) features.add(feature);
    }

    const device = await adapter.requestDevice({
      label: options.label ?? 'design-ai',
      requiredFeatures: [...features],
      // ORT Web's WebGPU EP needs raised limits (large storage buffers, big
      // workgroup memory). Asking for the adapter's own maxima is always
      // grantable and keeps our device usable as ORT's device.
      requiredLimits: maximalLimits(adapter),
    });

    const isFallback =
      (adapter as GPUAdapter & { isFallbackAdapter?: boolean }).isFallbackAdapter ?? false;
    return new GpuContext(device, isFallback, options);
  }

  get queue(): GPUQueue {
    return this.device.queue;
  }

  get lost(): GPUDeviceLostInfo | null {
    return this.#lost;
  }

  // --- recording -----------------------------------------------------------

  /**
   * The open command encoder, creating one if needed.
   *
   * Closes any open compute pass first: an encoder is locked while a pass is
   * open, so recording a copy or a `clearBuffer` without ending the pass is a
   * validation error. Doing it here rather than at every call site removes a
   * whole class of "worked until someone reordered two operators" bugs.
   */
  encoder(): GPUCommandEncoder {
    this.#assertUsable();
    this.endPass();
    this.#encoder ??= this.device.createCommandEncoder({ label: 'design-ai' });
    return this.#encoder;
  }

  /**
   * The open compute pass. Passes are kept open across operators so consecutive
   * dispatches share one pass; WebGPU still guarantees ordering and memory
   * visibility between them.
   */
  computePass(): GPUComputePassEncoder {
    if (this.#pass) return this.#pass;
    const encoder = this.encoder();
    this.#pass = encoder.beginComputePass({ label: 'design-ai-compute' });
    return this.#pass;
  }

  /** Closes the open compute pass; call before recording copies. */
  endPass(): void {
    if (this.#pass) {
      this.#pass.end();
      this.#pass = null;
    }
  }

  /** Records one dispatch. `resources` map to `@group(0) @binding(i)` in order. */
  dispatch(
    kernel: KernelRequest,
    resources: BindResource[],
    workgroups: readonly [number, number?, number?],
    label?: string,
  ): void {
    if (resources.length !== kernel.bindings.length) {
      throw new Error(
        `[gpu-core] ${kernel.module}: ${resources.length} resources for ${kernel.bindings.length} declared bindings`,
      );
    }
    const pipeline = this.shaders.pipeline(kernel);
    const bindGroup = this.device.createBindGroup({
      label: label ?? kernel.module,
      layout: this.shaders.bindGroupLayout(kernel.bindings),
      entries: resources.map((resource, binding) => ({
        binding,
        resource: normalizeResource(resource),
      })),
    });

    const [x, y = 1, z = 1] = workgroups;
    if (x <= 0 || y <= 0 || z <= 0) return; // Nothing to do; skip the empty dispatch.

    const passLabel = label ?? `${kernel.module}:${kernel.entryPoint ?? 'main'}`;
    const pass = this.#beginTimedPass(passLabel);
    pass.setPipeline(pipeline);
    pass.setBindGroup(0, bindGroup);
    pass.dispatchWorkgroups(x, y, z);
    this.#endTimedPass();
  }

  /** Writes a uniform block into the ring and returns a binding for it. */
  uniform(data: ArrayBufferView): GPUBufferBinding {
    return this.uniforms.write(data);
  }

  /** Submits recorded work. Cheap to call when nothing was recorded. */
  flush(): void {
    this.endPass();
    if (!this.#encoder) return;
    if (this.#profiling && this.#querySet && this.#queryIndex > 0) {
      this.#resolveQueries(this.#encoder);
    }
    const commands = this.#encoder.finish();
    this.#encoder = null;
    this.queue.submit([commands]);
    this.uniforms.reset();
    if (this.#profiling) void this.#readTimings();
  }

  /** Flushes and waits for the GPU to finish. */
  async sync(): Promise<void> {
    this.flush();
    await this.queue.onSubmittedWorkDone();
  }

  // --- resources -----------------------------------------------------------

  /** Pooled texture; call `release()` to recycle it. */
  image(width: number, height: number, format: TexFormat, label?: string): GpuImage {
    this.#assertUsable();
    return this.textures.acquire(width, height, format, label);
  }

  /** Pooled storage buffer usable as an ONNX tensor backing store. */
  storageBuffer(byteSize: number, extraUsage = 0, label?: string): GPUBuffer {
    this.#assertUsable();
    return this.buffers.acquire(
      byteSize,
      GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC | GPUBufferUsage.COPY_DST | extraUsage,
      label,
    );
  }

  releaseBuffer(buffer: GPUBuffer): void {
    this.buffers.release(buffer);
  }

  // --- profiling -----------------------------------------------------------

  /** Enables per-dispatch timestamps when the device supports them. */
  setProfiling(enabled: boolean, capacity = 128): void {
    if (enabled && !this.caps.timestampQuery) {
      this.#profiling = false;
      return;
    }
    this.#profiling = enabled;
    if (enabled && (!this.#querySet || this.#queryCapacity < capacity * 2)) {
      this.#querySet?.destroy();
      this.#queryCapacity = capacity * 2;
      this.#querySet = this.device.createQuerySet({
        label: 'design-ai-timestamps',
        type: 'timestamp',
        count: this.#queryCapacity,
      });
    }
  }

  /** Timings from the most recent completed submit. */
  get timings(): readonly PassTiming[] {
    return this.#lastTimings;
  }

  #beginTimedPass(label: string): GPUComputePassEncoder {
    if (!this.#profiling || !this.#querySet || this.#queryIndex * 2 + 1 >= this.#queryCapacity) {
      return this.computePass();
    }
    // Timestamps attach to a pass, so profiling mode gives each dispatch its own.
    this.endPass();
    const encoder = this.encoder();
    this.#queryLabels.push(label);
    const pass = encoder.beginComputePass({
      label,
      timestampWrites: {
        querySet: this.#querySet,
        beginningOfPassWriteIndex: this.#queryIndex * 2,
        endOfPassWriteIndex: this.#queryIndex * 2 + 1,
      },
    });
    this.#queryIndex++;
    this.#pass = pass;
    return pass;
  }

  #endTimedPass(): void {
    if (this.#profiling) this.endPass();
  }

  #resolveBuffers: { resolve: GPUBuffer; read: GPUBuffer; labels: string[] } | null = null;

  #resolveQueries(encoder: GPUCommandEncoder): void {
    const count = this.#queryIndex * 2;
    const byteSize = count * 8;
    const resolve = this.device.createBuffer({
      label: 'timestamp-resolve',
      size: byteSize,
      usage: GPUBufferUsage.QUERY_RESOLVE | GPUBufferUsage.COPY_SRC,
    });
    const read = this.device.createBuffer({
      label: 'timestamp-read',
      size: byteSize,
      usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ,
    });
    encoder.resolveQuerySet(this.#querySet!, 0, count, resolve, 0);
    encoder.copyBufferToBuffer(resolve, 0, read, 0, byteSize);
    this.#resolveBuffers = { resolve, read, labels: this.#queryLabels };
    this.#queryLabels = [];
    this.#queryIndex = 0;
  }

  async #readTimings(): Promise<void> {
    const pending = this.#resolveBuffers;
    if (!pending) return;
    this.#resolveBuffers = null;
    try {
      await pending.read.mapAsync(GPUMapMode.READ);
      const stamps = new BigUint64Array(pending.read.getMappedRange().slice(0));
      pending.read.unmap();
      this.#lastTimings = pending.labels.map((label, i) => ({
        label,
        durationNs: Number(stamps[i * 2 + 1]! - stamps[i * 2]!),
      }));
    } catch {
      // Device lost or buffer destroyed mid-flight; timings are best effort.
    } finally {
      pending.resolve.destroy();
      pending.read.destroy();
    }
  }

  // --- teardown ------------------------------------------------------------

  #assertUsable(): void {
    if (this.#destroyed) throw new Error('[gpu-core] context has been destroyed');
    if (this.#lost) {
      throw new Error(`[gpu-core] GPU device lost: ${this.#lost.reason} ${this.#lost.message}`);
    }
  }

  destroy(): void {
    if (this.#destroyed) return;
    this.#destroyed = true;
    this.endPass();
    this.#encoder = null;
    this.textures.trim();
    this.buffers.trim();
    this.uniforms.destroy();
    this.#querySet?.destroy();
    if (this.#ownsDevice) this.device.destroy();
  }
}

function normalizeResource(resource: BindResource): GPUBindingResource {
  if (resource instanceof GpuImage) return resource.view;
  if (typeof GPUBuffer !== 'undefined' && resource instanceof GPUBuffer) return { buffer: resource };
  return resource as GPUBindingResource;
}

/** Requests every limit the adapter supports, so ORT never hits a lower ceiling. */
function maximalLimits(adapter: GPUAdapter): Record<string, number> {
  const limits: Record<string, number> = {};
  // `GPUSupportedLimits` exposes its values as enumerable prototype accessors.
  for (const key in adapter.limits) {
    const value = (adapter.limits as unknown as Record<string, number>)[key];
    if (typeof value === 'number' && Number.isFinite(value)) limits[key] = value;
  }
  return limits;
}

/** Number of workgroups needed to cover `count` items at `size` per group. */
export function workgroupCount(count: number, size: number): number {
  return Math.ceil(Math.max(0, count) / size);
}
